import { ErrorText, Modal, Box } from "@cruk/cruk-react-components";
import dynamic from "next/dynamic";
import { useRouter } from "next/compat/router";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";

import { queryAsString } from "@fwa/src/utils/urlUtils";
import { fwsUrlFeedItem } from "@fwa/src/services/feedItemService";

import { ErrorBoundary } from "@fwa/src/components/ErrorBoundary";

import { type FeedItemType } from "@fwa/src/types";
import { useTracking } from "@fwa/src/hooks/useTracking";

const FeedItem = dynamic(() => import("@fwa/src/components/FeedItem"), {
  ssr: false,
});

export const FeedItemShared = ({
  feedId,
  pageId,
  pageUrl,
  isStravaMetric,
  fitbitDailyTargetSteps,
  sharedFeedItemData,
}: {
  feedId: string;
  pageId: string;
  pageUrl: string;
  isStravaMetric: boolean;
  fitbitDailyTargetSteps: number;
  sharedFeedItemData?: FeedItemType;
}) => {
  const { trackError } = useTracking();
  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { feed, slug, ...restOfQuery } = router?.query || {};
  const feedString = queryAsString(feed);

  const url =
    feedString.length || feedId
      ? `${fwsUrlFeedItem({ feedItemId: feedString || feedId })}`
      : null;

  // page cache
  const opts = { fallbackData: sharedFeedItemData };
  const { data, error, mutate } = useSwrGet<FeedItemType>(url, opts);
  const showModal = Boolean(data) && !error;

  const handleClose = () => {
    router
      ?.push({
        pathname: router.asPath.split("?")[0],
        query: { ...restOfQuery },
      })
      .catch((err) => {
        trackError(err as Error, { component: "FeedItemShared" });
      });
  };

  const mutateOnEdit = (updatedItem: FeedItemType) => {
    mutate(updatedItem).catch((err) => {
      trackError(err as Error, { component: "FeedItemShared" });
    });
  };

  if (showModal) {
    return (
      <Modal
        closeFunction={handleClose}
        showCloseButton
        modalName="shared feeditem"
        startOpen
      >
        <Box marginTop="xxl" padding="xs">
          {!error && data && (
            <ErrorBoundary>
              <FeedItem
                feedItem={data}
                pageUrl={pageUrl}
                pageId={pageId}
                isStravaMetric={isStravaMetric}
                fitbitDailyTargetSteps={fitbitDailyTargetSteps}
                handleDelete={() => {
                  // placeholder no op
                }}
                handleEdit={mutateOnEdit}
              />
            </ErrorBoundary>
          )}
          {error ? <ErrorText>Unable to find feed item</ErrorText> : null}
        </Box>
      </Modal>
    );
  }
  return null;
};

export default FeedItemShared;
